// ── List scroll memory ──────────────────────────────────────────────────────
// Keeps the results list exactly where the user left it when they open a
// listing and come back.
//
// Flow: the card click handler (GridCard / ListView) calls rememberListScroll()
// BEFORE routing to /view-post/:slug, recording the page scroll offset plus the
// clicked card's id and its distance from the top of the viewport. When Views
// re-mounts on Back it calls takeListScroll() for the same route and hands the
// entry to restoreListScroll(), which re-applies the offset once the list has
// rendered.
//
// Why anchor on the card and not just the scroll offset: the list is re-fetched
// on Back and its images load asynchronously, so raw pixel offsets drift as rows
// above the anchor grow. Re-solving "put card X back at Y px from the top of
// the viewport" converges on the same visual position instead. The raw offset is
// kept as the fallback for when the card is no longer in the results.
//
// Do NOT use window.scrollY / window.scrollTo() here. This app scrolls <body>
// (body is overflow-y:auto while <html> stays viewport-height), so the window
// never scrolls: window.scrollY reads a constant 0 and window.scrollTo() is a
// silent no-op. Everything below goes through the element that actually
// scrolls, resolved by getScroller().

const STORAGE_KEY = "listScrollMemory";

// Anything older than this is a stale tab, not a Back click.
const TTL_MS = 30 * 60 * 1000;

// How long we keep re-asserting the position after the list renders. Images
// and lazily-sized rows keep shifting the layout for a beat after the data
// lands; each retry re-solves the anchor offset.
const RETRY_DELAYS_MS = [0, 50, 150, 300, 600, 1000];

function isScrollable(el) {
  if (!el) return false;
  const overflowY = getComputedStyle(el).overflowY;
  return (
    (overflowY === "auto" || overflowY === "scroll") &&
    el.scrollHeight > el.clientHeight + 1
  );
}

// The element the page actually scrolls. Today that is <body>, but resolve it
// at runtime rather than hard-coding: a layout change that moves scrolling to
// <html> or to an inner pane keeps working instead of silently doing nothing.
function defaultScroller() {
  const root = document.scrollingElement || document.documentElement;
  if (root && root.scrollHeight > root.clientHeight + 1) return root;
  if (document.body && document.body.scrollHeight > document.body.clientHeight + 1) {
    return document.body;
  }
  return root || document.body;
}

// Nearest scrolling ancestor of a card, falling back to the page scroller.
function getScroller(el) {
  let node = el && el.parentElement;
  while (node && node !== document.documentElement) {
    if (isScrollable(node)) return node;
    node = node.parentElement;
  }
  return defaultScroller();
}

function readScrollTop(scroller) {
  return (scroller && scroller.scrollTop) || 0;
}

function writeScrollTop(scroller, top) {
  if (!scroller) return;
  if (typeof scroller.scrollTo === "function") {
    scroller.scrollTo({ top, left: 0, behavior: "auto" });
  } else {
    scroller.scrollTop = top;
  }
}

// servicesID comes from the DB and is interpolated into a CSS selector, so keep
// it to characters that can't break out of the attribute selector.
function findAnchor(servicesID) {
  if (!servicesID || !/^[\w.:-]+$/.test(servicesID)) return null;
  try {
    return document.querySelector(`[data-service-id="${servicesID}"]`);
  } catch (_) {
    return null;
  }
}

/**
 * Record the current list position. Call synchronously from the click handler,
 * before any await — `el` is only valid for the duration of the event.
 *
 * @param {object}  opts
 * @param {string}  opts.path        route path these results belong to
 * @param {*}       opts.servicesID  id of the clicked listing
 * @param {Element} [opts.el]        the clicked card/row element
 */
export function rememberListScroll({ path, servicesID, el } = {}) {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        path: path || "/",
        servicesID: servicesID == null ? null : String(servicesID),
        y: readScrollTop(getScroller(el)),
        anchorTop:
          el && typeof el.getBoundingClientRect === "function"
            ? el.getBoundingClientRect().top
            : null,
        savedAt: Date.now(),
      })
    );
  } catch (_) {
    /* private mode / quota — scroll memory is best-effort */
  }
}

/**
 * Read and consume the saved position, but only if it belongs to `path`.
 * Always clears the entry so a position is restored at most once.
 */
export function takeListScroll(path) {
  let raw = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw);
    if (!entry || entry.path !== (path || "/")) return null;
    if (!entry.savedAt || Date.now() - entry.savedAt > TTL_MS) return null;
    return entry;
  } catch (_) {
    return null;
  }
}

/**
 * Jump the list back to the top — for pagination, where the user asked for a
 * new page and expects to start at its first row.
 *
 * Exists because the obvious `window.scrollTo({ top: 0 })` does nothing here;
 * see the scroller note at the top of this file.
 */
export function scrollListToTop(behavior = "smooth") {
  const scroller = defaultScroller();
  if (!scroller) return;
  if (typeof scroller.scrollTo === "function") {
    scroller.scrollTo({ top: 0, left: 0, behavior });
  } else {
    scroller.scrollTop = 0;
  }
}

export function clearListScroll() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    /* nothing to clear */
  }
}

/**
 * Re-apply a saved position. Returns a cancel function; the restore also
 * cancels itself the moment the user scrolls, so it never fights real input.
 */
export function restoreListScroll(entry) {
  if (!entry) return () => {};

  const timers = [];
  let cancelled = false;

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    timers.forEach(clearTimeout);
    timers.length = 0;
    // Only user-initiated scroll gestures — a plain "scroll" listener would
    // fire on our own scrollTo and abort the retries immediately.
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchstart", cancel);
    window.removeEventListener("keydown", cancel);
  };

  window.addEventListener("wheel", cancel, { passive: true });
  window.addEventListener("touchstart", cancel, { passive: true });
  window.addEventListener("keydown", cancel);

  const apply = () => {
    if (cancelled) return;
    const anchor = findAnchor(entry.servicesID);
    // Resolve the scroller from the anchor so an inner scrolling pane is
    // handled the same way as the page itself.
    const scroller = getScroller(anchor);
    const current = readScrollTop(scroller);
    const top =
      anchor && entry.anchorTop != null
        ? Math.max(
            0,
            Math.round(current + anchor.getBoundingClientRect().top - entry.anchorTop)
          )
        : Math.max(0, Math.round(entry.y || 0));
    if (Math.abs(current - top) > 1) {
      writeScrollTop(scroller, top);
    }
  };

  RETRY_DELAYS_MS.forEach((delay, i) => {
    timers.push(
      setTimeout(() => {
        apply();
        if (i === RETRY_DELAYS_MS.length - 1) cancel();
      }, delay)
    );
  });

  return cancel;
}
